import type { ApiError as ApiErrorShape } from '@fint/shared-types';
import { useAuthStore } from '@/stores/auth';

const API_BASE = '/v1';

/**
 * A failed request, with the server's envelope attached.
 *
 * `errors` carries Zod's field-level detail, so a form can mark the offending
 * input instead of showing one banner for a five-field failure.
 */
export class ApiError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string,
    public readonly errors?: Array<{ path: string; message: string }>,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The message for one field, if the server blamed it. */
  fieldError(path: string): string | undefined {
    return this.errors?.find((e) => e.path === path)?.message;
  }
}

/**
 * EVERY server call goes through here. Never `fetch` from a component.
 *
 * This is what attaches the org header, handles a dead session in one place,
 * and gives every caller the same error type. A component doing its own fetch
 * gets none of that, and the failure shows up as a page that silently renders
 * another tenant's empty state.
 */
async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const { activeOrgId, isAuthenticated } = useAuthStore.getState();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) ?? {}),
  };
  // The active book, unless the caller named one. The Inbox spans every
  // book and confirms into each under ITS OWN id - see `RequestOptions`.
  if (activeOrgId && !headers['x-org-id']) headers['x-org-id'] = activeOrgId;
  // A caller that sets Content-Type to undefined is asking the browser to
  // choose - which is the only correct answer for multipart, whose boundary
  // token only the browser knows. See `api.upload`.
  for (const [key, value] of Object.entries(headers)) {
    if (value === undefined) delete headers[key];
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    // The session is an httpOnly cookie, so it only travels if we ask for it.
    // Without this the app authenticates on exactly nothing.
    credentials: 'include',
  });

  // 204 and friends have no body; parsing one would throw on success.
  const data: Partial<ApiErrorShape> & Record<string, unknown> =
    res.status === 204 ? {} : await res.json().catch(() => ({}));

  if (!res.ok) {
    // A wrong MFA code 401s on the MFA routes, and that must not eject a user
    // who IS signed in and merely mistyped six digits. A genuinely dead session
    // also 401s there, so the two are told apart by the server's message.
    const wrongMfaCode = path.startsWith('/auth/mfa/') && /mfa code/i.test(String(data.message ?? ''));

    if (res.status === 401 && isAuthenticated && !wrongMfaCode) {
      useAuthStore.getState().clear();
      // A full navigation, not a router push: the whole React tree is holding
      // data for a session that no longer exists.
      window.location.href = '/login';
    }

    throw new ApiError(
      res.status,
      String(data.message ?? 'Something went wrong'),
      data.errors as Array<{ path: string; message: string }> | undefined,
      data.code as string | undefined,
    );
  }

  return data as T;
}

/**
 * `orgId` sends the request AS another of the person's books.
 *
 * The one screen that needs it is the Inbox: it lists every book at once,
 * and a confirm or a move is decided by the book the row is in, not the one
 * the sidebar happens to show. Explicit per call rather than a store switch,
 * so nothing else on the page changes tenant behind the person's back.
 */
export interface RequestOptions {
  orgId?: string;
}

const asOrg = (options?: RequestOptions): RequestInit =>
  options?.orgId ? { headers: { 'x-org-id': options.orgId } } : {};

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, asOrg(options)),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...asOrg(options), method: 'POST', body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...asOrg(options), method: 'PUT', body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...asOrg(options), method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...asOrg(options), method: 'DELETE' }),

  /**
   * Send a file as multipart.
   *
   * ── THE ONE THING THAT MATTERS HERE ──
   * `Content-Type` is deliberately set to undefined so `request` skips it and
   * the BROWSER sets the header itself. A multipart content type carries a
   * generated boundary token, and the browser is the only party that knows what
   * it is. Sending `application/json` - which every other call here does, and
   * which would otherwise be inherited - produces a request the server parses
   * as an empty body, so the upload arrives as "no file was uploaded" with
   * nothing in the console to explain it.
   */
  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<T>(path, {
      method: 'POST',
      body: form,
      headers: { 'Content-Type': undefined as unknown as string },
    });
  },
};

/**
 * Build a query string, dropping empty values.
 *
 * Without the drop, an untouched filter sends `?q=&status=` and the server has
 * to decide whether an empty string means "no filter" or "match empty" - a
 * decision better not to force.
 */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}
